'use strict';

const geocode = require('./components/deliveryModal/geocode');
const formValidation = require('./components/formValidation');
const { handleStateSelection, checkCoverage, showAddressErrorMessage, clearNotCoverageMessages } = require('./util/addressForm');
const addressValidation = require('./util/addressValidation');

/**
 * Restricts postal code inputs to digits and strips non-digit leftovers on
 * blur. Shared by the My Account and Sidebar add/edit address forms.
 *
 * @param {jQuery} $form - The address form element.
 * @returns {void}
 */
function initFormValidation($form) {
    formValidation.initOnKeypressValidation($form, '[name*="_postalCode"]', /[0-9]/, /\D/g);
}

var STREET_SELECTION_KEY = 'fgGoogleSuggestionStreet';
var SUGGESTION_RESET_MARKER = 'fgAddressSuggestionReset';
var pendingSuggestionClick = false;
var suggestionClickGuardBound = false;

/**
 * after a Google suggestion has filled the address
 * fields, if the shopper edits Calle and leaves the field WITHOUT choosing
 * a new suggestion, the seven suggestion-owned fields (Calle, Numero
 * Exterior, Numero Interior, Colonia, Codigo Postal, Estado, Ciudad) are reset to an
 * empty, error-free state (no classes, no feedback, no locks, no residual
 * aria-invalid). No validation is triggered immediately: the centralized
 *  rules resume on the existing input/change/focusout bridge events
 * and the submit-time full validation stays untouched.
 *
 * Detection uses the real event order of the custom geocode dropdown:
 * clicking a .pac-item fires mousedown on the item BEFORE the focused
 * Calle blurs (mousedown -> focusout -> click -> async getPlaceDetails ->
 * autofillInputs). The transient module flag distinguishes both exit
 * causes without touching the geocode dropdown itself, so:
 *   - a dropdown selection never resets (the flag, or the synthetic blur
 *     of the street fill, is consumed instead);
 *   - leaving Calle unchanged after a selection never resets (value still
 *     equals the recorded selection);
 *   - plain focus without any prior selection never resets.
 *
 * @param {jQuery} $form - The address form element.
 * @param {Function} [onSuggestionInvalidated] - Optional callback called with the
 *        form when a previous Google suggestion is invalidated by an edit.
 * @returns {void}
 */
function initSuggestionReset($form, onSuggestionInvalidated) {
    if (!$form || !$form.length) {
        return;
    }

    if (!suggestionClickGuardBound) {
        $(document).on('mousedown.fgAddressSuggestion', '.pac-custom-container .pac-item', function () {
            if ($(this).closest('form').data(SUGGESTION_RESET_MARKER)) {
                pendingSuggestionClick = true;
            }
        });
        suggestionClickGuardBound = true;
    }

    if ($.data($form[0], SUGGESTION_RESET_MARKER)) {
        return;
    }
    $.data($form[0], SUGGESTION_RESET_MARKER, true);

    $form.on('focusout.fgAddressSuggestion', function (e) {
        var wasPendingClick = pendingSuggestionClick;
        pendingSuggestionClick = false;

        var $street = $(e.target);
        if (wasPendingClick || !$street.is('[name$=_address1]')) {
            return;
        }

        var selectedStreet = $street.data(STREET_SELECTION_KEY);
        if (selectedStreet === undefined || $street.val() === selectedStreet) {
            return;
        }

        $street.removeData(STREET_SELECTION_KEY);
        clearComponentField($street);
        clearComponentField($form.find('[name$=_externalNumber]'));
        clearComponentField($form.find('[name$=_interiorNumber]'));
        clearComponentField($form.find('[name$=_neighborhood]'));
        clearComponentField($form.find('[name$=_postalCode]'));
        clearComponentField($form.find('[name$=_stateCode]'));
        clearComponentField($form.find('[name$=_city]'));
        $form.find('[name$=_address1], [name$=_externalNumber], [name$=_interiorNumber], [name$=_neighborhood], ' +
                   '[name$=_postalCode], [name$=_stateCode], [name$=_city]')
             .removeAttr('aria-invalid');

        if (typeof onSuggestionInvalidated === 'function') {
            onSuggestionInvalidated($form);
        }
    });
}

/**
 * Silently empties a component field that the selected Google place did not
 * provide : no change/blur is emitted so the shared 
 * engine never marks the absent component as required, and any stale value,
 * error state, filled-label state or disabled flag left by a previous
 * selection is removed. The DOM operations mirror the clearing half of
 * renderAddressErrors.
 *
 * @param {jQuery} $field - The managed field.
 * @returns {void}
 */
function clearComponentField($field) {
    $field.val('');
    $field.removeClass('is-invalid');
    $field.parent().removeClass('is-invalid filled');
    var $feedback = $field.siblings('.invalid-feedback');
    if ($feedback.length) {
        $feedback.text('');
    }
    $field.prop('disabled', false);
}

/**
 * Writes a component provided by the selected place and emits the standard
 * change + blur events, exactly like the legacy autofill writes do, so the
 * 2857 engine revalidates the field and formFields recomputes .filled.
 * Optionally locks the field afterwards (AC3: only when Google provided a
 * value). Blur runs before the lock so the final validation pass always
 * happens on an enabled element.
 *
 * @param {jQuery} $field - The managed field.
 * @param {string} value - Component value provided by the place.
 * @param {boolean} [lock] - Disable the field after filling it.
 * @returns {void}
 */
function fillComponent($field, value, lock) {
    $field.val(value).trigger('change').blur();
    if (lock) {
        $field.prop('disabled', true);
    }
}

/**
 * Resolves the six managed address fields of a form using the project's
 * name-suffix convention, shared by both autofill policies.
 *
 * @param {jQuery} $form - The address form element.
 * @returns {Object} jQuery collections keyed by managed field.
 */
function resolveAddressFields($form) {
    return {
        $neighborhood: $form.find('[name$=_neighborhood]'),
        $city: $form.find('[name$=_city]'),
        $state: $form.find('[name$=_stateCode]'),
        $postalCode: $form.find('[name$=_postalCode]'),
        $street: $form.find('[name$=_address1]'),
        $externalNumber: $form.find('[name$=_externalNumber]')
    };
}

/**
 * Interactive suggestion policy: provided components are written with
 * fillComponent, missing ones are silently cleared, city and postal code
 * are locked, and the street text owned by this selection is recorded
 * before the street fill so a later edit-without-selection can reset it.
 *
 * @param {Object} fields - Result of resolveAddressFields.
 * @param {Object} addressDetails - Output of addressModel.getAddressComponents.
 * @returns {void}
 */
function applySuggestionComponents(fields, addressDetails) {
    const { city, state, neighborhood, postalCode, streetName, streetNumber } = addressDetails;
    const { $neighborhood, $city, $state, $postalCode, $street, $externalNumber } = fields;

    if (streetNumber) {
        fillComponent($externalNumber, streetNumber);
    } else {
        clearComponentField($externalNumber);
    }

    if (neighborhood) {
        fillComponent($neighborhood, neighborhood);
    } else {
        clearComponentField($neighborhood);
    }

    if (city) {
        fillComponent($city, city, true);
    } else {
        clearComponentField($city);
    }

    // A state value that matches no official option behaves like a missing
    // one: empty, editable and free of stale locks from a previous selection.
    if (!state || !handleStateSelection(state, $state)) {
        clearComponentField($state);
    }

    if (postalCode) {
        fillComponent($postalCode, postalCode, true);
    } else {
        clearComponentField($postalCode);
    }

    $street.data(STREET_SELECTION_KEY, streetName || $street.val());

    if (streetName) {
        fillComponent($street, streetName);
    }
}

/**
 * Programmatic fill policy (base or migrated addresses): only provided
 * components are written, keeping the historical change + blur events;
 * colonia and city are additionally cleared when the new place omits them.
 * No locking of postal code and no selection recording.
 *
 * @param {Object} fields - Result of resolveAddressFields.
 * @param {Object} addressDetails - Output of addressModel.getAddressComponents.
 * @returns {void}
 */
function applyProgrammaticComponents(fields, addressDetails) {
    const { city, state, neighborhood, postalCode, streetName, streetNumber } = addressDetails;
    const { $neighborhood, $city, $state, $postalCode, $street, $externalNumber } = fields;

    if (!neighborhood && $neighborhood.val()) {
        $neighborhood.val('').trigger('change').blur();
    }

    if (!city && $city.val()) {
        $city.val('').trigger('change').blur();
        $city.prop('disabled', false);
    }

    //Colonia
    if (neighborhood) {
        $neighborhood.val(neighborhood).trigger('change').blur();
    }

    if (streetName) {
        $street.val(streetName).trigger('change').blur();
    }

    if (streetNumber) {
        $externalNumber.val(streetNumber).trigger('change').blur();
    }

    if (city) {
        $city.val(city).trigger('change').blur().prop('disabled', true);
    }

    if (state) {
        handleStateSelection(state, $state);
    }

    if (postalCode) {
        $postalCode.val(postalCode).trigger('change').blur();
    }
}

/**
 * Entry point that applies geocoded details to the address fields of a form.
 * The details object is the shared addressModel.getAddressComponents contract.
 * lockComponents selects between the interactive suggestion policy and the
 * legacy programmatic policy; both end with syncAddressFields, which refreshes
 * any previously flagged field that the fill itself did not touch.
 *
 * @param {jQuery} $form - The address form element.
 * @param {Object} addressDetails - Output of addressModel.getAddressComponents.
 * @param {boolean} [lockComponents] - Suggestion-selection mode: the place is
 *        authoritative, missing components silently clear their field and
 *        postalCode/state/city are locked only when Google provided them.
 *        Programmatic callers (base-address fill, coverage flow) keep the
 *        legacy fill-only contract by omitting it.
 * @returns {void}
 */
function autofillInputs($form, addressDetails, lockComponents) {
    const fields = resolveAddressFields($form);

    if (lockComponents) {
        applySuggestionComponents(fields, addressDetails);
    } else {
        applyProgrammaticComponents(fields, addressDetails);
    }

    addressValidation.syncAddressFields($form);
}

/**
 * Clears a previously shown coverage error once the user types in the form.
 * Body of the keyup handlers previously duplicated in both callers
 * (the My Account handler checked #zipCode and the Sidebar one checked
 * .postalcodeTxt: the same input element in the shared form markup).
 *
 * @param {jQuery} $form - The address form element (closest('form') of input).
 * @returns {void}
 */
function clearCoverageErrorOnEdit($form) {
    const $zipCode = $form.find('#zipCode');
    const $messageContainer = $form.find('.address-form-error_container');

    if ($zipCode.hasClass('address-form-error') || $messageContainer.hasClass('address-form-error--no-postal')) {
        clearNotCoverageMessages($form);
    }
}

/**
 *
 * Returns false when the flow must stop (invalid fields, ZIP mismatch or no
 * geocoded place), or { formData, place } when the caller-specific coverage
 * check / AJAX submission must proceed with the original code.
 *
 * @param {jQuery} $form - The submitted address form.
 * @returns {Promise<Object|false>} Prologue result as described above.
 */
async function validateAndLocateAddress($form) {
    // Google-unavailable message (no postal error attached): clear it on every submit
    // so a retry re-runs the normal flow instead of staying behind a disabled button.
    if ($form.find('.address-form-error_container.address-form-error--no-postal').length) {
        clearNotCoverageMessages($form);
    }

    if (!addressValidation.validateAddressForm($form, 'account')) {
        return false;
    }

    const formData = $form.serializeArray();

    const addressDetails = geocode.buildAddressDetailsFromForm($form);
    const postalCode = geocode.getPostalCodeFromForm($form);
    const place = await geocode.fetchPlaceFromAddress(addressDetails, postalCode);
    if (place && place.postalCodeMismatch) {
        showAddressErrorMessage($form, { isTechnicalError: false, errorMsg: $form.data('postal-code-mismatch-msg') });
        return false;
    }

    // Google Maps/Places unavailable: fetchPlaceFromAddress resolves undefined.
    // Show the technical message without highlighting the postal code.
    if (place === undefined) {
        showAddressErrorMessage($form, {
            isTechnicalError: true,
            markPostalError: false,
            errorMsg: $form.data('google-maps-unavailable-msg')
        });
        return false;
    }

    // A resolved null means the geocoder ran but found no match (ZERO_RESULTS):
    // stop without treating it as a Google unavailability.
    if (!place) {
        return false;
    }

    return { formData: formData, place: place };
}

/**
 * Shared coverage step of both submit handlers: calls the coverage service
 * for the geocoded place with the original 'editAddress' context and, when
 * the address has no coverage, displays the same error message both callers
 * used to display inline.
 *
 * Returns true when the flow may continue, false when the caller must stop
 * (the message was already rendered). Service rejections are deliberately
 * NOT caught here: My Account keeps its own try/catch around this call and
 * the Sidebar lets them propagate, exactly as before centralization.
 *
 * @param {Object} place - Geocoded place from validateAndLocateAddress.
 * @param {jQuery} $form - The submitted address form.
 * @returns {Promise<boolean>} Whether the address has coverage.
 */
async function checkAddressCoverage(place, $form) {
    const coverageResult = await checkCoverage(place, $form, 'editAddress');

    if (!coverageResult.hasCoverage) {
        showAddressErrorMessage($form, coverageResult);
        return false;
    }

    return true;
}

/**
 * Appends the geocoded coordinates to the serialized form data, exactly as
 * the two submit handlers used to do inline. Each caller keeps ownership of
 * its module-level coords object and passes the values in.
 *
 * @param {Array} formData - Serialized form fields (from serializeArray).
 * @param {Object} coords - Object potentially holding lat/lng values.
 * @returns {void}
 */
function appendFormCoordinates(formData, coords) {
    if (typeof coords !== 'undefined' && coords.lat && coords.lng) {
        formData.push({ name: 'latitude', value: coords.lat });
        formData.push({ name: 'longitude', value: coords.lng });
    }
}

module.exports = {
    initFormValidation: initFormValidation,
    initSuggestionReset: initSuggestionReset,
    autofillInputs: autofillInputs,
    clearCoverageErrorOnEdit: clearCoverageErrorOnEdit,
    validateAndLocateAddress: validateAndLocateAddress,
    checkAddressCoverage: checkAddressCoverage,
    appendFormCoordinates: appendFormCoordinates
};
